<template>
  <div class="avatar" :class="[imageSize]" :style="sizeStyle">
    <div class="avatar--border" :style="sizeStyle">
      <div class="avatar--image" :style="borderSize">
        <img
            v-if="imageStatus===2"
            :src="imageUrl"
            :alt="alt"
            :title="title"
            v-on:error="onImageError()"
            :style="borderSize"
        >
        <div v-else-if="imageStatus===1" class="img-avatar skeleton" :style="borderSize"></div>
        <span v-else class="avatar--name" :style="borderSize">{{ fullNameAvatar }}</span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'AvatarWidget',
  props: {
    imageKey: {
      type: String,
      required: false
    },
    socialAvatar: {
      type: String,
      required: false
    },
    imageSize: {
      type: String,
      required: true,
      default: 'imageM'
    },
    fullNameAvatar: {
      type: String,
      required: true
    },
    alt: {
      type: String,
      required: false
    },
    title: {
      type: String,
      required: false
    }
  },
  computed: {
    imageUrl(): string {
      if (this.imageKey) {
        return `${this.imageKey}/${this.imageSize}`;
      } else if (this.socialAvatar) {
        return this.socialAvatar;
      } else {
        return '';
      }
    },
    sizeStyle(): { width: string; height: string; minWidth: string; minHeight: string } {
      const map: Record<string, string> = {
        imageXXS: '20px',
        imageXS: '32px',
        imageSM: '40px',
        imageM: '80px',
        imageL: '140px',
      };

      const size = map[this.imageSize] || '140px';
      return {
        width: size,
        height: size,
        minWidth: size,
        minHeight: size
      };
    },
    borderSize(): { width: string; height: string; minWidth: string; minHeight: string } {
      const map: Record<string, string> = {
        imageXXS: '18px',
        imageXS: '30px',
        imageSM: '38px',
        imageM: '78px',
        imageL: '136px',
      };

      const size = map[this.imageSize] || '136px';
      return {
        width: size,
        height: size,
        minWidth: size,
        minHeight: size
      };
    }
  },
  data() {
    return {
      imageStatus: 0
    };
  },
  beforeMount() {
    if (this.imageKey || this.socialAvatar) {
      this.imageStatus = 2;
    }
  },
  methods: {
    onImageError() {
      this.imageStatus = 0;
    }
  }
});

</script>
<style lang="scss" scoped>

.avatar {
  display: flex;
  width: 100%;
  position: relative;
  cursor: pointer;
  &:hover {
    .avatar--border {
      background-color: var(--border-neutral-dark);
    }
    .avatar--image {
      background-color: var(--light-100);
    }
  }
  &.imageXXS{
    .avatar--name{
      font-size: var(--fs-10);
    }
    .avatar--border{
      background-color: var(--transparent);
      mask-image: none;
      &:hover{
        background-color: var(--transparent);
        mask-image: none;
        box-shadow: none;
      }
    }
  }
  &.imageXS{
    .avatar--name{
      font-size: var(--fs-10);
    }
  }
  &.imageS{
    .avatar--name{
      font-size: var(--fs-16);
    }
  }
  &.imageM{
    .avatar--name{
      font-size: var(--fs-18);
    }
  }
  &.imageL{
    .avatar--name{
      font-size: var(--fs-48);
    }
  }
  &.imageXL{
    .avatar--name{
      font-size: var(--fs-48);
    }
  }

  .avatar--border{
    mask-image: url("data:image/svg+xml,%3Csvg clip-rule='evenodd' fill-rule='evenodd' viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='m18.947 18.947c-13.32 13.32-17.947 42.57-17.947 109.053s4.626 95.732 17.947 109.053c13.32 13.32 42.57 17.947 109.053 17.947s95.732-4.626 109.053-17.947c13.32-13.32 17.947-42.57 17.947-109.053s-4.626-95.732-17.947-109.053c-13.32-13.32-42.57-17.947-109.053-17.947s-95.732 4.626-109.053 17.947z' fill-rule='nonzero' stroke='%23000' transform='matrix(.992126 0 0 .992126 1.00787 1.00787)'/%3E%3C/svg%3E");
    background-color: var(--border-neutral-dark);
    display: flex;
    justify-content: center;
    align-items: center;
    &:hover{
      background-color: var(--border-neutral);
      box-shadow: var(--shadow-outline);
    }
  }
  .avatar--image {
    background-color: var(--light-100);
    border-radius: $br-sm;
    display: flex;
    justify-content: center;
    align-items: center;
    color: var(--text-neutral);
    font-size: var(--fs-16);
    font-weight: 600;
    mask-image: url("data:image/svg+xml,%3Csvg clip-rule='evenodd' fill-rule='evenodd' viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='m18.947 18.947c-13.32 13.32-17.947 42.57-17.947 109.053s4.626 95.732 17.947 109.053c13.32 13.32 42.57 17.947 109.053 17.947s95.732-4.626 109.053-17.947c13.32-13.32 17.947-42.57 17.947-109.053s-4.626-95.732-17.947-109.053c-13.32-13.32-42.57-17.947-109.053-17.947s-95.732 4.626-109.053 17.947z' fill-rule='nonzero' stroke='%23000' transform='matrix(.992126 0 0 .992126 1.00787 1.00787)'/%3E%3C/svg%3E");
    mask-position: center center;
    mask-repeat: no-repeat;

    img {
      border-radius: $br-sm;
    }
  }
  .avatar--name {
    display: flex;
    justify-content: center;
    align-items: center;
    text-transform: uppercase;
  }
}

</style>
